import React from 'react';
import { useApp } from '../context/AppContext';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, ShoppingCart, ArrowRight, X } from 'lucide-react';

export const CartSuccessModal: React.FC = () => {
  const { cartModalOpen, setCartModalOpen, cartModalItem, setPage } = useApp();

  if (!cartModalOpen || !cartModalItem) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setCartModalOpen(false)}
          className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        />

        {/* Modal dialog */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 15 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 15 }}
          transition={{ type: 'spring', duration: 0.4 }}
          className="relative w-full max-w-md bg-[#FEFCE8] dark:bg-[#1C1A14] border-2 border-amber-200/90 dark:border-amber-700/60 rounded-3xl shadow-2xl p-6 overflow-hidden z-10"
        >
          {/* Close button */}
          <button
            onClick={() => setCartModalOpen(false)}
            className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-amber-200/60 dark:hover:bg-amber-900/40 text-amber-800/60 dark:text-amber-300 hover:text-amber-950 dark:hover:text-amber-100 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Success Header */}
          <div className="flex flex-col items-center text-center space-y-3 pt-1">
            <div className="h-14 w-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-sm shadow-emerald-500/10">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <div>
              <h3 className="font-display font-black text-xl text-slate-900 dark:text-amber-100 flex items-center justify-center gap-1.5">
                Course Added Successfully
              </h3>
              <p className="text-sm font-medium text-amber-900/80 dark:text-amber-200/80 mt-1">
                Your course has been added to your Shopping Cart.
              </p>
            </div>
          </div>

          {/* Course Details Preview */}
          <div className="my-6 p-4 bg-white dark:bg-[#26231B] rounded-2xl border border-amber-200/90 dark:border-amber-800/60 shadow-sm flex items-center gap-4">
            <div className="w-24 h-14 rounded-xl overflow-hidden bg-slate-900 shrink-0 border border-amber-200 dark:border-amber-900/40 shadow-xs">
              <img
                src={cartModalItem.image || '/images/courses/c6s1.webp'}
                alt={cartModalItem.title}
                loading="lazy"
                className="w-full h-full object-cover object-left"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src.endsWith('.webp')) {
                    target.src = target.src.replace(/\.webp$/, '.jpg');
                  } else if (target.src.endsWith('/images/courses/c7s1.jpg')) {
                    target.src = '/c7s1.jpg';
                  } else if (target.src.endsWith('/images/courses/c6s1.jpg')) {
                    target.src = '/c6s1.jpg';
                  } else if (!target.src.endsWith('/c6s1.jpg')) {
                    target.src = '/c6s1.jpg';
                  }
                }}
              />
            </div>
            <div className="flex-grow min-w-0">
              <span className="text-[10px] font-extrabold text-amber-700 dark:text-amber-400 uppercase tracking-wider bg-amber-100/90 dark:bg-amber-950/70 px-2 py-0.5 rounded-md inline-block">
                Syllabus Pack
              </span>
              <p className="text-sm font-extrabold text-slate-900 dark:text-slate-100 truncate mt-1">
                {cartModalItem.title}
              </p>
            </div>
            <div className="text-right font-mono font-black text-amber-600 dark:text-amber-400 shrink-0 text-base">
              ₹{cartModalItem.price}
            </div>
          </div>

          {/* Interactive Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button
              onClick={() => setCartModalOpen(false)}
              className="flex-1 py-3 px-4 bg-white hover:bg-amber-100/70 dark:bg-[#26231B] dark:hover:bg-[#322E23] text-slate-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700/60 rounded-xl font-bold text-xs cursor-pointer transition-all text-center shadow-xs"
            >
              Continue Shopping
            </button>
            <button
              onClick={() => {
                setCartModalOpen(false);
                setPage('cart');
              }}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 rounded-xl font-black text-xs flex items-center justify-center gap-1.5 cursor-pointer shadow-md shadow-amber-500/20 transition-all text-center active:scale-[0.98]"
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              <span>Proceed to Cart</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
